import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ForwardedRef, ReactElement } from 'react'

import { useTheme } from '@mui/material'
import classNames from 'classnames'
import { matchSorter } from 'match-sorter'
import { useIntl } from 'react-intl'
import type { ActionMeta, MenuPlacement } from 'react-select'
import Select from 'react-select'

import withDisabled from '../../hoc/withDisabled/withDisabled'
import useFieldDisabledContext from '../../hooks/useFieldDisabledContext'
import type { Variant } from '../../utils/constants'
import { HELPER_TYPE_ERROR } from '../../utils/constants'
import testHandle from '../../utils/testHandle'
import type { DropdownOption } from '../../utils/types'
import HelperText from '../HelperText/HelperText'
import Label from '../Label/Label'
import { defaultComponents, paginatedComponents } from './SingleSearchSelect.components'
import { LabelAndInputWrapper } from '../../styles/form-control.styles'
import { selectStyles, SingleSearchSelectWrapper } from './SingleSelectSearch.styles'

export type SingleSearchSelectProps = {
  id: string
  options: DropdownOption[]
  label?: string | ReactElement
  value?: string | null
  onChange?: (value: string, meta?: ActionMeta<DropdownOption>) => any
  onBlur?: () => any
  error?: boolean
  disabled?: boolean
  message?: string
  placeholder?: string
  isMissing?: boolean
  isIncomplete?: boolean
  isClearable?: boolean
  verificationError?: string
  mapOptionsToSelectFormat?: boolean
  variant?: Variant
  controlShouldRenderValue?: boolean
  focusDefaultOption?: boolean
  highlightSelected?: boolean
  isPortal?: boolean
  isLoading?: boolean
  menuPlacement?: MenuPlacement
  paginated?: boolean
  pageSize?: number
}

const DEFAULT_PAGE_SIZE = 200

/**
 * @deprecated Use AutocompleteField, AutocompleteFieldFF instead
 */
const SingleSearchSelect = forwardRef(function SingleSearchSelect(
  {
    id,
    options = [],
    label,
    value,
    onChange,
    onBlur,
    error,
    disabled,
    message,
    placeholder,
    isMissing = false,
    isIncomplete = false,
    isClearable = true,
    verificationError,
    variant = 'default',
    controlShouldRenderValue = true,
    focusDefaultOption = true,
    highlightSelected = true,
    isPortal = false,
    isLoading = false,
    menuPlacement = 'bottom',
    paginated = false,
    pageSize = DEFAULT_PAGE_SIZE,
  }: SingleSearchSelectProps,
  // Ref target is typed as `any` because the outer `withDisabled` HOC's ref
  // generics can't accept a concrete DOM type here without a wider refactor of
  // the HOC — DynamicFilter's union generic unifies on the ref type.
  ref: ForwardedRef<any>,
) {
  const intl = useIntl()
  const theme = useTheme()
  const formatMessage = (id: string): string => intl.formatMessage({ id })
  const contextDisabled = useFieldDisabledContext()
  const wrapperClassName = classNames({ error })

  const defaultValue = useMemo(() => options.find(({ value: val }) => val === value), [options, value])

  const [stateOptions, setStateOptions] = useState<DropdownOption[]>(options || [])

  const [visibleCount, setVisibleCount] = useState(pageSize)
  const visibleCountRef = useRef(visibleCount)
  visibleCountRef.current = visibleCount

  useEffect(() => {
    setStateOptions(options)
  }, [options])

  const handleChange = useCallback(
    (value: DropdownOption | readonly DropdownOption[] | null | undefined, action: ActionMeta<DropdownOption>) => {
      onChange && onChange(value ? (value as DropdownOption).value : '', action)
    },
    [onChange],
  )

  const styles = useMemo(() => selectStyles(theme, variant, highlightSelected), [theme, variant, highlightSelected])

  const onInputChange = useCallback(
    (inputValue: string) => {
      if (paginated) {
        setVisibleCount(pageSize)
      }
      if (!inputValue) {
        setStateOptions(options)
      } else {
        setStateOptions(
          matchSorter(options, inputValue, {
            keys: [{ threshold: matchSorter.rankings.CONTAINS, key: 'label' }],
          }),
        )
      }
    },
    [options, paginated, pageSize],
  )

  const handleScrollToBottom = useCallback(() => {
    setVisibleCount((prev) => prev + pageSize)
  }, [pageSize])

  const isDisabled = disabled !== undefined ? disabled : contextDisabled

  const visibleOptions =
    paginated && stateOptions.length > visibleCount ? stateOptions.slice(0, visibleCount) : stateOptions

  return (
    <SingleSearchSelectWrapper data-testid={testHandle(id)} className={wrapperClassName} ref={ref}>
      <LabelAndInputWrapper variant={variant}>
        {label && (
          <Label
            htmlFor={id}
            isDisabled={false}
            text={label}
            isMissing={isMissing}
            isIncomplete={isIncomplete}
            verificationError={verificationError}
          />
        )}
        <Select
          id={id}
          aria-label={label ? String(label) : id}
          styles={styles}
          isClearable={isClearable}
          tabSelectsValue={false}
          isDisabled={isDisabled}
          onInputChange={onInputChange}
          onBlur={onBlur}
          options={visibleOptions}
          classNamePrefix="react-select"
          onChange={handleChange}
          defaultValue={defaultValue}
          placeholder={placeholder}
          autocomplete="false"
          data-sl="exclude"
          components={paginated ? paginatedComponents : defaultComponents}
          isLoading={isLoading}
          // @ts-ignore - need to pass in empty string (otherwise upon passing null, the old value persists on screen), issue with react-select
          value={value ? defaultValue : ''}
          focusDefaultOption={focusDefaultOption}
          controlShouldRenderValue={controlShouldRenderValue}
          menuPortalTarget={isPortal ? document.body : null}
          menuPlacement={menuPlacement}
          onMenuScrollToBottom={paginated ? handleScrollToBottom : undefined}
        />
      </LabelAndInputWrapper>
      {message && (
        <HelperText id={id} message={message || ''} textType={error ? HELPER_TYPE_ERROR : ''} variant={variant} />
      )}
      {isMissing && (
        <HelperText
          id={`${id}Helper`}
          message={formatMessage('errors.missingFieldRequired')}
          textType={HELPER_TYPE_ERROR}
          variant={variant}
        />
      )}
    </SingleSearchSelectWrapper>
  )
})

export default withDisabled(SingleSearchSelect)
